import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioSection, NioButton } from '../../../components';
import { useTranslation } from '../../../i18n/I18nProvider';
import { formatFileSize } from '../../../utils/formatFileSize';

const MAX_FILES = 4;
const MAX_FILE_SIZE_BYTES = 7 * 1024 * 1024;

function RequestDemandStep5() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();

  const { t } = useTranslation();

  const selectedSubService = location.state?.selectedSubService || t('common.subService');
  const parentServiceTitle = location.state?.parentServiceTitle || t('common.service');
  const projectTitle = location.state?.projectTitle || '';
  const requestDetails = location.state?.requestDetails || {};
  const requestStep4Details = location.state?.requestStep4Details || {};
  const isStep5Skipped = location.state?.isStep5Skipped || false;

  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const canContinue = files.length > 0;

  const subtitle = useMemo(() => {
    if (!files.length) return t('wizard.step5Hint');
    return t('wizard.step5FilesSelected', { count: files.length });
  }, [files.length, t]);

  const handleFiles = (event) => {
    const incoming = Array.from(event.target.files || []);
    if (!incoming.length) return;

    const merged = [...files];
    let nextError = '';

    incoming.forEach((file) => {
      if (merged.length >= MAX_FILES) {
        nextError = t('wizard.step5MaxFiles');
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        nextError = t('wizard.step5FileTooLarge', { name: file.name });
        return;
      }
      merged.push(file);
    });

    setFiles(merged);
    setError(nextError);
    event.target.value = '';
  };

  const removeFileAt = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setError('');
  };

  return (
    <AppLayout title={t('wizard.step5Title')} rootClass="layout-1">
      <style>{`
        .request-step5-page {
          background: #f2f3f5;
          min-height: 78vh;
          border-radius: 14px;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .request-step5-content {
          flex: 1 1 auto;
          width: 100%;
          max-width: 760px;
          margin: 0 auto;
          padding-top: 4.2rem;
          padding-bottom: 2rem;
        }
        .request-step5-step {
          color: #697586;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }
        .request-step5-heading {
          color: #0f2b3d;
          font-weight: 700;
          margin-bottom: 0.4rem;
        }
        .request-step5-description {
          color: #5f6b7a;
          margin-bottom: 2rem;
        }
        .request-step5-dropzone {
          width: 100%;
          border: 1px solid #d6dde5;
          border-radius: 10px;
          background: #fff;
          padding: 1rem 1.1rem;
          display: flex;
          align-items: center;
          gap: 0.9rem;
          cursor: pointer;
          text-align: left;
        }
        .request-step5-icon {
          width: 56px;
          height: 56px;
          border-radius: 8px;
          background: #edf2f7;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #2563eb;
          font-size: 1.4rem;
          flex-shrink: 0;
        }
        .request-step5-dropzone-title {
          color: #0f2b3d;
          font-weight: 600;
          margin-bottom: 0.2rem;
        }
        .request-step5-dropzone-subtitle {
          color: #5f6b7a;
          font-size: 0.92rem;
          margin: 0;
        }
        .request-step5-list {
          margin-top: 1rem;
          display: grid;
          gap: 0.6rem;
        }
        .request-step5-item {
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          padding: 0.6rem 0.8rem;
          display: flex;
          justify-content: space-between;
          gap: 0.7rem;
          align-items: center;
        }
        .request-step5-file {
          color: #0f2b3d;
          font-weight: 500;
          word-break: break-word;
        }
        .request-step5-meta {
          color: #6f7d8c;
          font-size: 0.86rem;
        }
        .request-step5-remove {
          border: none;
          background: transparent;
          color: #d14343;
          font-weight: 600;
          padding: 0;
        }
        .request-step5-error {
          margin-top: 0.8rem;
          color: #d14343;
          font-size: 0.9rem;
        }
        .request-step5-footer {
          flex-shrink: 0;
          margin-top: auto;
          border-top: 1px solid #dde3ea;
          padding: 0.7rem 1.1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8f9fb;
          border-bottom-left-radius: 14px;
          border-bottom-right-radius: 14px;
          z-index: 5;
          box-shadow: 0 -4px 16px rgba(15, 43, 61, 0.08);
        }
      `}</style>

      <NioSection className="pt-120 pt-lg-160 pb-4">
        <NioSection.Content>
          <div className="request-step5-page p-3 p-md-4">
            <div className="request-step5-content">
              <div className="request-step5-step">{t('wizard.step', { n: 5 })}</div>
              <h2 className="request-step5-heading">{t('wizard.step5Heading')}</h2>
              <p className="request-step5-description">
                {t('wizard.step5Description')}
              </p>

              <label htmlFor="request-step5-files" className="request-step5-dropzone">
                <div className="request-step5-icon">+</div>
                <div>
                  <div className="request-step5-dropzone-title">{t('wizard.step5Dropzone')}</div>
                  <p className="request-step5-dropzone-subtitle">{subtitle}</p>
                </div>
              </label>
              <input
                id="request-step5-files"
                type="file"
                accept="image/*,.pdf"
                multiple
                onChange={handleFiles}
                hidden
              />

              {files.length > 0 && (
                <div className="request-step5-list">
                  {files.map((file, index) => (
                    <div key={`${file.name}-${file.size}-${index}`} className="request-step5-item">
                      <div>
                        <div className="request-step5-file">{file.name}</div>
                        <div className="request-step5-meta">{formatFileSize(file.size, t)}</div>
                      </div>
                      <button
                        type="button"
                        className="request-step5-remove"
                        onClick={() => removeFileAt(index)}
                      >
                        {t('common.remove')}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {error && <div className="request-step5-error">{error}</div>}
            </div>

            <div className="request-step5-footer">
              <NioButton
                className="btn btn-primary"
                label={t('common.back')}
                onClick={() => navigate(-1)}
              />
              <NioButton
                className={`btn ${canContinue ? 'btn-primary' : 'btn-primary disabled'}`}
                label={t('common.next')}
                onClick={() => {
                  if (!canContinue) return;
                  const firstImageFile = files.find((file) => file.type?.startsWith('image/'));
                  const selectedImageUrl = firstImageFile ? URL.createObjectURL(firstImageFile) : '';
                  navigate(`/service/${serviceId}/request-demand-step6`, {
                    state: {
                      selectedSubService,
                      parentServiceTitle,
                      projectTitle,
                      isStep5Skipped,
                      requestDetails,
                      requestStep4Details,
                      requestStep5Details: {
                        documents: files.map((file) => ({
                          name: file.name,
                          size: file.size,
                          type: file.type,
                        })),
                        selectedImageUrl,
                      },
                    },
                  });
                }}
              />
            </div>
          </div>
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}

export default RequestDemandStep5;
